import type { Bounds, LatLng } from "@oshimap/domain";
import { isPointInKorea, type BasemapProvider, type BasemapProviderKind, type MapDataProviderKind } from "./provider";

export type RouteProviderKind = "google" | "naver";
export type GeocodingProviderKind = "google";
/** 외부 장소 검색 공급자는 아직 채택하지 않았습니다. */
export type PlaceSearchProviderKind = "google" | "naver";

/** 외부 검색 결과는 Domain Place가 아니며 식별자를 노출하지 않습니다. */
export interface LocationCandidate {
  readonly label: string;
  readonly address: string;
  readonly point: LatLng;
}
export interface RouteRequest {
  readonly origin: LatLng;
  readonly destination: LatLng;
  readonly mode: "walking" | "driving" | "transit";
}
export interface RouteResult {
  readonly path: readonly LatLng[];
  readonly distanceMeters: number;
  readonly durationSeconds: number;
}
export interface RouteProvider {
  route(request: RouteRequest): Promise<RouteResult>;
}
export interface GeocodingProvider {
  geocode(address: string): Promise<readonly LocationCandidate[]>;
  reverseGeocode(point: LatLng): Promise<readonly LocationCandidate[]>;
}
export interface PlaceSearchProvider {
  search(query: string, near: LatLng): Promise<readonly LocationCandidate[]>;
}
export interface MapDataFeature {
  readonly label: string;
  readonly geometry: readonly LatLng[];
}
export interface MapDataProvider {
  query(bounds: Bounds): Promise<readonly MapDataFeature[]>;
}
export interface CapabilityKinds {
  basemap: BasemapProviderKind;
  route: RouteProviderKind;
  geocoding: GeocodingProviderKind;
  placeSearch: PlaceSearchProviderKind;
  mapData: MapDataProviderKind;
}
export interface CapabilityContracts {
  basemap: BasemapProvider;
  route: RouteProvider;
  geocoding: GeocodingProvider;
  placeSearch: PlaceSearchProvider;
  mapData: MapDataProvider;
}
export type MapCapability = keyof CapabilityKinds;
export interface SelectionLocation {
  readonly point: LatLng;
  /** 명시적 국가가 있으면 우선합니다. 좌표 범위는 국가 SSOT가 아닙니다. */
  readonly countryCode?: string;
}
export type CapabilityPolicy = { readonly [C in MapCapability]: readonly CapabilityKinds[C][] };

/** 후보 정책은 구현 여부와 무관합니다. 경로의 지역은 출발지를 기준으로 합니다. */
export function capabilityPolicyForLocation(location: SelectionLocation): CapabilityPolicy {
  const korea = location.countryCode !== undefined
    ? location.countryCode.toUpperCase() === "KR" : isPointInKorea(location.point);
  return {
    basemap: ["google"],
    route: korea ? ["naver"] : ["google"],
    geocoding: ["google"],
    placeSearch: [],
    mapData: korea ? ["vworld"] : [],
  };
}

/** SDK 준비와 설정 검증을 마친 구현체만 앱의 구성 경계에서 등록합니다.
 * 키나 SDK 응답은 이 계약에 넣지 않습니다. 키 존재만으로 등록하지 않습니다.
 */
export type CapabilityRegistry = {
  readonly [C in MapCapability]: Readonly<Partial<Record<CapabilityKinds[C], CapabilityContracts[C]>>>;
};
export function emptyCapabilityRegistry(): CapabilityRegistry {
  return { basemap: {}, route: {}, geocoding: {}, placeSearch: {}, mapData: {} };
}
export function selectCapabilityProvider<C extends MapCapability>(
  capability: C,
  location: SelectionLocation,
  registry: CapabilityRegistry,
): CapabilityKinds[C] | null {
  return capabilityPolicyForLocation(location)[capability].find((kind) => registry[capability][kind] != null) ?? null;
}

/**
 * 바닥 지도가 없는 이유입니다.
 *
 * "왜 지도가 안 보이는가"는 화면마다 다르게 말하면 안 됩니다. Web 과 Native 가
 * 같은 문장을 쓰도록 이유를 값으로 두고 문구를 여기서 만듭니다. 실제로
 * OD-003 이 닫힌 뒤에도 화면에는 "지도 제공자 미정"이 남아 있었습니다 —
 * 결정은 끝났는데 화면이 옛날을 말하고 있었습니다.
 */
export type BasemapAbsence = "NO_PROVIDER_SELECTED" | "NOT_CONFIGURED" | "SDK_UNAVAILABLE";

/** 등록된 basemap 구현이 없으면 그 이유를 돌려줍니다. 있으면 null 입니다. */
export function basemapAbsence(
  registry: CapabilityRegistry,
  location: SelectionLocation,
): BasemapAbsence | null {
  if (selectCapabilityProvider("basemap", location, registry) != null) return null;
  const policy = capabilityPolicyForLocation(location).basemap;
  // 정책이 고른 provider 자체가 없으면 '미선정', 있는데 구현이 등록되지 않았으면
  // '설정 안 됨'입니다. 등록은 SDK 준비와 설정 검증을 마친 뒤에만 일어납니다.
  return policy.length === 0 ? "NO_PROVIDER_SELECTED" : "NOT_CONFIGURED";
}

/** 사용자에게 그대로 보여줄 수 있는 문장입니다. */
export function basemapAbsenceMessage(reason: BasemapAbsence): string {
  switch (reason) {
    case "NO_PROVIDER_SELECTED":
      return "바닥 지도 제공자가 아직 정해지지 않았습니다.";
    case "NOT_CONFIGURED":
      // 무엇이 없는지 말합니다. "준비 중"은 아무것도 알려주지 않습니다.
      return "바닥 지도를 아직 연결하지 못했습니다 (지도 API 키 미설정).";
    case "SDK_UNAVAILABLE":
      return "바닥 지도를 불러오지 못했습니다.";
  }
}

/** 지도가 없어도 이 화면이 무엇을 해 주는지 함께 말합니다. */
export function basemapFallbackNote(reason: BasemapAbsence): string {
  return `${basemapAbsenceMessage(reason)} 위치와 거리는 실제 좌표로 계산해 표시합니다.`;
}
